import { Box, Skeleton, styled } from "@mui/material";
import React from "react";
import { MessageProps } from "../../../interfaces/all";
import { countEmojis } from "../../../utils/message";
import MessageAvatar from "./MessageAvatar";
import MessageMetaInfo from "./MessageMetaInfo";
import {
  MessageContainer,
  MessageContent,
  MessageSender,
  MessageTextLine,
} from "./styles";

const EmojiContent = styled(Box, {
  shouldForwardProp: (prop) => prop !== "emojiCount",
})<{ emojiCount: number }>(({ emojiCount }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  userSelect: "none",
  fontSize:
    emojiCount === 1
      ? "3.2rem"
      : emojiCount === 2
        ? "2.6rem"
        : emojiCount === 3
          ? "2rem"
          : "1.4rem",
  lineHeight: 1,
  gap: emojiCount <= 3 ? "0.25rem" : "0.15rem",
  padding: emojiCount <= 3 ? "0.5rem 0" : "0.5rem 0",
  width: emojiCount <= 3 ? "100%" : "auto",
  minHeight: emojiCount <= 3 ? "2.5rem" : "auto",
  textAlign: "center",
  flexWrap: "nowrap",
  overflow: "hidden",
  position: "relative",
  paddingBottom: "0.6875rem",
}));

interface EmojiMessagePropsSkeleton extends MessageProps {
  showSkeleton?: boolean;
}

const EmojiMessage: React.FC<EmojiMessagePropsSkeleton> = ({
  message,
  showAvatar = true,
  showDate = true,
  showSkeleton = false,
}) => {
  const isFromClient = message.sentBy === "client";
  const currentReadStatus = message.readStatus || { isRead: false };
  const emojiCount = countEmojis(message.content.text || "");

  const getSenderName = () => {
    const formatName = (name: string) => {
      return name
        .split(" ")
        .map(
          (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
        )
        .join(" ");
    };

    let name: string;
    switch (message.sentBy) {
      case "client":
        name = "Cliente";
        return formatName(name);
      case "user":
        name = "Atendente";
        return formatName(name);
      default:
        return "Usuário";
    }
  };

  if (showSkeleton) {
    return (
      <MessageContainer isFromClient={isFromClient} showTriangle={showAvatar}>
        {isFromClient ? (
          <>
            <MessageAvatar sentBy={message.sentBy} showAvatar={showAvatar} />
            <MessageContent
              isFromClient={isFromClient}
              showTriangle={showAvatar}
            >
              <MessageSender isFromClient={isFromClient}>
                <Skeleton variant="text" width={80} height={18} />
              </MessageSender>
              <MessageTextLine
                style={{
                  position: "relative",
                  paddingRight: 62,
                  minHeight: 24,
                }}
              >
                <EmojiContent emojiCount={1}>
                  <Skeleton
                    variant="rectangular"
                    width={64}
                    height={64}
                    sx={{ borderRadius: 8 }}
                  />
                </EmojiContent>
                <MessageMetaInfo
                  message={message}
                  showDate={showDate}
                  isFromClient={isFromClient}
                  readStatus={currentReadStatus}
                />
              </MessageTextLine>
            </MessageContent>
          </>
        ) : (
          <>
            <MessageContent
              isFromClient={isFromClient}
              showTriangle={showAvatar}
            >
              <MessageSender isFromClient={isFromClient}>
                <Skeleton variant="text" width={80} height={18} />
              </MessageSender>
              <MessageTextLine
                style={{
                  position: "relative",
                  paddingRight: 62,
                  minHeight: 24,
                }}
              >
                <EmojiContent emojiCount={1}>
                  <Skeleton
                    variant="rectangular"
                    width={64}
                    height={64}
                    sx={{ borderRadius: 8 }}
                  />
                </EmojiContent>
                <MessageMetaInfo
                  message={message}
                  showDate={showDate}
                  isFromClient={isFromClient}
                  readStatus={currentReadStatus}
                />
              </MessageTextLine>
            </MessageContent>
            <MessageAvatar sentBy={message.sentBy} showAvatar={showAvatar} />
          </>
        )}
      </MessageContainer>
    );
  }

  return (
    <MessageContainer isFromClient={isFromClient} showTriangle={showAvatar}>
      {isFromClient ? (
        <>
          <MessageAvatar sentBy={message.sentBy} showAvatar={showAvatar} />
          <MessageContent isFromClient={isFromClient} showTriangle={showAvatar}>
            {showAvatar && (
              <MessageSender isFromClient={isFromClient}>
                {getSenderName()}
              </MessageSender>
            )}
            <MessageTextLine
              style={{ position: "relative", paddingRight: 62, minHeight: 24 }}
            >
              <EmojiContent emojiCount={emojiCount}>
                {message.content.text}
              </EmojiContent>
              <MessageMetaInfo
                message={message}
                showDate={showDate}
                isFromClient={isFromClient}
                readStatus={currentReadStatus}
              />
            </MessageTextLine>
          </MessageContent>
        </>
      ) : (
        <>
          <MessageContent isFromClient={isFromClient} showTriangle={showAvatar}>
            {showAvatar && (
              <MessageSender isFromClient={isFromClient}>
                {getSenderName()}
              </MessageSender>
            )}
            <MessageTextLine
              style={{ position: "relative", paddingRight: 62, minHeight: 24 }}
            >
              <EmojiContent emojiCount={emojiCount}>
                {message.content.text}
              </EmojiContent>
              <MessageMetaInfo
                message={message}
                showDate={showDate}
                isFromClient={isFromClient}
                readStatus={currentReadStatus}
              />
            </MessageTextLine>
          </MessageContent>
          <MessageAvatar sentBy={message.sentBy} showAvatar={showAvatar} />
        </>
      )}
    </MessageContainer>
  );
};

export default EmojiMessage;
